import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

const emptyItem = () => ({
  item_name: '',
  description: '',
  quantity: 1,
  cost: 0,
  total: 0,
});

export default function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    type: 'general',
    title: '',
    filter_by: '',
    due_date: '',
    status: 'draft',
  });
  const [items, setItems] = useState([emptyItem()]);
  const [memberIds, setMemberIds] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);

  const fetchMembers = useCallback(async (searchTerm) => {
    setMemberSearchLoading(true);
    try {
      const res = await api.get('/members', {
        params: { search: searchTerm || undefined, limit: 50, status: 'all' },
      });
      setMemberOptions(res.data.data || []);
    } catch {
      setMemberOptions([]);
    } finally {
      setMemberSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchMembers(memberSearch), 300);
    return () => clearTimeout(timer);
  }, [memberSearch, fetchMembers]);

  useEffect(() => {
    if (id) {
      fetchInvoice();
    }
  }, [id]);


  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/finance/invoices/${id}`);
      const inv = response.data.data;
      setFormData({
        type: inv.type || 'general',
        title: inv.title || '',
        filter_by: inv.filter_by || '',
        due_date: inv.due_date ? inv.due_date.split('T')[0] : '',
        status: inv.status || 'draft',
      });
      setItems(
        inv.items?.length
          ? inv.items.map((i) => ({
              item_name: i.item_name || '',
              description: i.description || '',
              quantity: parseFloat(i.quantity) || 1,
              cost: parseFloat(i.cost) || 0,
              total: parseFloat(i.total) || 0,
            }))
          : [emptyItem()]
      );
      setMemberIds(inv.member_ids || []);
      if (inv.member_ids?.length) {
        fetchMembers('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'cost') {
      const qty = parseFloat(field === 'quantity' ? value : newItems[index].quantity) || 0;
      const cost = parseFloat(field === 'cost' ? value : newItems[index].cost) || 0;
      newItems[index].total = qty * cost;
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, emptyItem()]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const addMember = (member) => {
    if (!member || memberIds.includes(member.id)) return;
    setMemberIds([...memberIds, member.id]);
    setMemberSearch('');
  };

  const removeMember = (memberId) => {
    setMemberIds(memberIds.filter((id) => id !== memberId));
  };

  const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.title?.trim()) {
      setError('Title is required');
      return;
    }
    const validItems = items.filter((i) => i.item_name?.trim());
    if (validItems.length === 0) {
      setError('At least one item with a name is required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        items: validItems.map((i) => ({
          item_name: i.item_name.trim(),
          description: i.description?.trim() || null,
          quantity: parseFloat(i.quantity) || 1,
          cost: parseFloat(i.cost) || 0,
        })),
        member_ids: memberIds,
      };
      if (id) {
        await api.put(`/finance/invoices/${id}`, payload);
      } else {
        await api.post('/finance/invoices', payload);
      }
      navigate('/dashboard/invoices');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id && items.length === 1 && !items[0].item_name) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedMembersForDisplay = memberIds.map((mid) => {
    const m = memberOptions.find((o) => o.id === mid);
    return m ? { id: mid, label: `${m.first_name} ${m.last_name}` } : { id: mid, label: mid };
  });

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>{id ? 'Edit Invoice' : 'New Invoice'}</Box>
          </>
        }
        title={id ? 'Edit Invoice' : 'New Invoice'}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/invoices')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Invoices
          </Button>
        }
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g. Membership Dues 2024"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select name="type" value={formData.type} onChange={handleChange} label="Type">
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="dues">Dues</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={formData.status} onChange={handleChange} label="Status">
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Filter By"
                name="filter_by"
                value={formData.filter_by}
                onChange={handleChange}
                placeholder="Optional filter (e.g. constituency)"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Invoice Items
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        Cost (₵)
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ width: 50 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Item name"
                            value={item.item_name}
                            onChange={(e) => updateItem(idx, 'item_name', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateItem(idx, 'description', e.target.value)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 90 }}
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 110 }}
                            value={item.cost || ''}
                            onChange={(e) => updateItem(idx, 'cost', e.target.value)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            ₵{(parseFloat(item.total) || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => removeItem(idx)}
                            disabled={items.length <= 1}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button startIcon={<AddIcon />} onClick={addItem} sx={{ mt: 1 }} size="small">
                Add Item
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Members (optional)
              </Typography>
              <Autocomplete
                options={memberOptions.filter((m) => !memberIds.includes(m.id))}
                getOptionLabel={(m) => `${m.first_name || ''} ${m.last_name || ''} (${m.member_id || m.email || ''})`}
                loading={memberSearchLoading}
                inputValue={memberSearch}
                onInputChange={(_, v) => setMemberSearch(v)}
                onChange={(_, val) => val && addMember(val)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Search and add members" />
                )}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {selectedMembersForDisplay.map((m) => (
                  <Chip
                    key={m.id}
                    label={m.label}
                    onDelete={() => removeMember(m.id)}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Total: ₵{totalAmount.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/dashboard/invoices')}>
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
